import { Component, Inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  laptopOptions,
  assetTypeOptions,
  historyFields,
} from './resource-profile.config';
import { SharedModule } from '../../../../../shared/shared.module';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule } from '@angular/material/table';
import { EmeraldDropdownModule } from '@emerald-angular/dropdown';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { AssetManagementService } from '@app/pages/asset-management/asset-management-service';
import { OffboardingDetails } from '@app/shared/interfaces/offboarding.model';
import { DropdownComponent } from '@app/shared/components/dropdown/dropdown.component';
import {
  DropdownOption,
  LOCAL_ACCESSORY_OPTIONS,
} from '@app/pages/asset-management/my-request/my-request-form/my-request-form.constants';
import { firstValueFrom } from 'rxjs';
import { StatusLabelComponent } from '@app/shared/components/Status-Label/status-label.component';
import { ToasterService } from '@app/shared/services/toaster.service';
import { AddCommentsComponent } from '../add-comments/add-comments.component';

export interface OffboardingDialogData {
  offboardingData: OffboardingDetails[];
  employeeId: string;
  record: OffboardingDetails;
}

@Component({
  standalone: true,
  imports: [
    SharedModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatExpansionModule,
    MatTableModule,
    EmeraldDropdownModule,
    ReactiveFormsModule,
    DropdownComponent,
    StatusLabelComponent,
  ],
  templateUrl: './resource-profile-page.component.html',
  styleUrl: './resource-profile-page.component.scss',
})
export class ResourceProfilePageComponent implements OnInit {
  laptopOptions = laptopOptions;
  assetTypeOptions = LOCAL_ACCESSORY_OPTIONS;
  accessoryOptions: DropdownOption[] = this.assetTypeOptions;
  form: FormGroup;
  offboardingData: OffboardingDetails[] = [];
  historyData: OffboardingDetails[] = [];
  historyFields = historyFields;
  isLoading = false;
  displayedColumns: string[] = [];

  get hasAssetHistory(): boolean {
    return (
      Array.isArray(this.historyData) &&
      this.historyData.some((h) => !!h?.lastHireDate)
    );
  }

  constructor(
    public dialogRef: MatDialogRef<ResourceProfilePageComponent>,
    private fb: FormBuilder,
    private assetManagementService: AssetManagementService,
    private toastrService: ToasterService,
    private readonly dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: OffboardingDialogData,
  ) {
    this.form = this.fb.group({
      fullName: [null],
      employeeId: [null],
      email: [null],
      employer: [null],
      lastHireDate: [null],
      location: [null],
      reportingManager: [null],
      dntLeader: [null],
      laptopType: [null],
      accessoriesType: [null],
      laptopSerialNumber: [null],
      chargerSerialNumber: [null],
    });
    this.form.disable();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  closeForm(): void {
    this.onCancel();
  }

  async ngOnInit(): Promise<void> {
    // Check if offboarding data was already passed from parent component
    if (this.data?.offboardingData && this.data.offboardingData.length > 0) {
      // Use pre-loaded data to avoid duplicate API call
      this.processOffboardingData(this.data.offboardingData);
    } else {
      // Fallback: make API call if data not provided
      const employeeId: any =
        this.data?.record && this.data.record.employeeId
          ? this.data.record.employeeId
          : this.data?.employeeId;
      this.getOffboardingData(employeeId);
    }
    await this.setupAccessoryOptions$();
  }
  getOffboardingData(employeeId: string): void {
    this.isLoading = true;
    this.assetManagementService.getOffboardingGetData(employeeId).subscribe({
      next: (response) => {
        this.processOffboardingData(response.data ?? []);
      },
      error: (error) => {
        this.isLoading = false;
      },
    });
  }

  private async setupAccessoryOptions$(): Promise<void> {
    try {
      const response: any = await firstValueFrom(
        this.assetManagementService.getAllAccessoriesAfterUpdate(),
      );
      if (Array.isArray(response?.data) && response.data.length > 0) {
        this.accessoryOptions = response.data.map((item: any) => ({
          label: item?.accessoriesType ?? '',
          value: item?.accessoriesType ?? '',
        }));
      } else {
        this.accessoryOptions = this.assetTypeOptions;
      }
    } catch (error) {
      this.accessoryOptions = this.assetTypeOptions;
    }
  }

  private processOffboardingData(offboardingData: OffboardingDetails[]): void {
    this.offboardingData = offboardingData;
    if (this.offboardingData.length > 0) {
      const dataIndex = this.offboardingData.findIndex(
        (item) => item.currentActiveFlag == true,
      );
      let data: OffboardingDetails;
      let history: OffboardingDetails[];
      if (dataIndex !== -1) {
        data = this.offboardingData[dataIndex];
        history = this.offboardingData
          .slice(0, dataIndex)
          .concat(this.offboardingData.slice(dataIndex + 1));
      } else {
        data = this.offboardingData[0];
        history = this.offboardingData.slice(1);
      }
      this.historyData = history;
      const accessoriesTypeValue: string[] = Array.isArray(data.accessoriesType)
        ? data.accessoriesType.flatMap((item: any) =>
            typeof item === 'string'
              ? item.split(',').map((v: string) => v.trim())
              : [],
          )
        : typeof data.accessoriesType === 'string'
          ? data.accessoriesType.split(',').map((v: string) => v.trim())
          : [];

      this.form.patchValue({
        fullName: data.fullName ?? '',
        employeeId: data.employeeId ?? '',
        email: data.email ?? '',
        employer: data.employer ?? '',
        lastHireDate: data.lastHireDate ?? '',
        location: data.location ?? '',
        reportingManager: data.reportingManager ?? '',
        dntLeader: data.dntLeader ?? '',
        laptopSerialNumber: data.laptopSerialNumber ?? '',
        chargerSerialNumber: data.chargerSerialNumber ?? '',
        laptopType: data.laptopType ?? '',
        accessoriesType: accessoriesTypeValue,
      });
    }
    this.isLoading = false;
  }

  onStatusChanged(event: any) {
    const dialogRef = this.openDownloadConfirmDialog();

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        // User cancelled: revert dropdown to previous state by reloading data
        this.getOffboardingData(this.data.employeeId);
        return;
      }

      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const formattedToday = `${yyyy}-${mm}-${dd}`;

      this.assetManagementService
        .updateReturnStatus({
          employeeId: this.data.employeeId,
          newReturnStatus: event,
          newReturnDate: formattedToday,
        })
        .subscribe({
          next: () => {
            this.toastrService.showSuccess(
              'Delivery status successfully updated',
              'Success',
            );
            this.getOffboardingData(this.data.employeeId);
          },
          error: () => {
            // Revert dropdown to previous state on API error
            this.getOffboardingData(this.data.employeeId);
          },
        });
    });
  }

  openDownloadConfirmDialog() {
    const dialogRef = this.dialog.open(AddCommentsComponent, {
      data: {
        option: 'confirmation',
        content: 'Are you sure you want to change the return status?',
      },
    });

    return dialogRef;
  }
}
